import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  FiUsers,
  FiShield,
  FiKey,
  FiSave,
  FiEdit,
  FiTrash2,
  FiPlus,
  FiSearch,
  FiUser,
  FiMail,
  FiLock,
  FiCheckCircle,
  FiXCircle,
  FiRefreshCw,
  FiEye,
  FiEdit2,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import axios from "axios";
import "./Settings.css";
// const url = import.meta.env.VITE_BACKEND_URL;
const url = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [authSettings, setAuthSettings] = useState({
    minPasswordLength: 8,
    passwordExpiration: 90,
    sessionTimeout: 30,
    maxLoginAttempts: 5
  });

  // Fetch users from backend
  const fetchUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${url}/api/auth`);
      if (response.data.success) {
        setUsers(response.data.data);
      } else {
        setError("Failed to fetch users");
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err.response?.data?.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  // Fetch authentication settings (mock data for now)
  const fetchAuthSettings = async () => {
    try {
      // In a real app, you would fetch these from your backend
      const mockSettings = {
        minPasswordLength: 8,
        passwordExpiration: 90,
        sessionTimeout: 30,
        maxLoginAttempts: 5
      };
      setAuthSettings(mockSettings);
    } catch (err) {
      console.error("Error fetching auth settings:", err);
    }
  };

  useEffect(() => {
    if (activeTab === "users" && currentUser?.role === "developer") {
      fetchUsers();
    }
    if (activeTab === "auth") {
      fetchAuthSettings();
    }
  }, [activeTab, currentUser]);

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      const response = await axios.delete(
        `${url}/api/auth/${userId}`
      );
      if (response.data.success) {
        setUsers(users.filter((user) => user._id !== userId));
        setSuccess("User deleted successfully");
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      setError("Failed to delete user");
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      const response = await axios.patch(
        `${url}/api/auth/${userId}/status`,
        {
          isActive: !currentStatus,
        }
      );

      if (response.data.success) {
        setUsers(
          users.map((user) =>
            user._id === userId ? { ...user, isActive: !currentStatus } : user
          )
        );
        setSuccess(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      console.error("Error updating user status:", err);
      setError("Failed to update user status");
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({ ...user });
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      const response = await axios.put(
        `${url}/api/auth/${formData._id}`,
        formData
      );
      
      if (response.data.success) {
        setUsers(users.map(user => 
          user._id === formData._id ? formData : user
        ));
        setIsModalOpen(false);
        setSuccess("User updated successfully");
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      console.error("Error updating user:", err);
      setError("Failed to update user");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAuthSettingChange = (e) => {
    const { name, value } = e.target;
    setAuthSettings({ ...authSettings, [name]: parseInt(value) });
  };

  const handleSaveAuthSettings = async () => {
    try {
      // In a real app, you would save these to your backend
      setSuccess("Authentication settings saved successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error saving auth settings:", err);
      setError("Failed to save authentication settings");
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setIsEditing(false);
  };

  const renderUserModal = () => {
    if (!selectedUser) return null;

    if (isEditing) {
      return (
        <div className="modal-content">
          <h2>Edit User</h2>
          
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              name="name"
              value={formData.name || ""}
              onChange={handleInputChange}
              placeholder="Full Name"
            />
          </div>
          
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email || ""}
              onChange={handleInputChange}
              placeholder="Email"
            />
          </div>
          
          <div className="form-group">
            <label>Role</label>
            <select
              name="role"
              value={formData.role || ""}
              onChange={handleInputChange}
            >
              <option value="user">User</option>
              <option value="developer">developer</option>
            </select>
          </div>
          
          <div className="modal-actions">
            <button className="btn-primary" onClick={handleSaveUser}>
              Save Changes
            </button>
            <button className="btn-secondary" onClick={closeModal}>
              Cancel
            </button>
          </div>
        </div>
      );
    }

    // View mode
    return (
      <div className="modal-content">
        <div className="user-modal-header">
          <div className="user-avatar-large">
            {selectedUser.name ? selectedUser.name.charAt(0).toUpperCase() : 'U'}
          </div>
          <div className="user-modal-info">
            <h2>{selectedUser.name || "No Name"}</h2>
            <p>{selectedUser.email}</p>
          </div>
        </div>
        
        <div className="user-details">
          <div className="detail-item">
            <span className="detail-label">Role:</span>
            <span className="detail-value">{selectedUser.role || "user"}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Status:</span>
            <span className={`detail-value status-${selectedUser.isActive ? 'active' : 'inactive'}`}>
              {selectedUser.isActive ? "Active" : "Inactive"}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Created:</span>
            <span className="detail-value">
              {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : "N/A"}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Last Login:</span>
            <span className="detail-value">
              {selectedUser.lastLogin ? new Date(selectedUser.lastLogin).toLocaleString() : "Never"}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Last Logout:</span>
            <span className="detail-value">
              {selectedUser.lastLogout ? new Date(selectedUser.lastLogout).toLocaleString() : "Never"}
            </span>
          </div>
        </div>
        
        <div className="modal-actions">
          <button className="btn-primary" onClick={() => handleEditUser(selectedUser)}>
            Edit User
          </button>
          <button className="btn-secondary" onClick={closeModal}>
            Close
          </button>
        </div>
      </div>
    );
  };

  const renderUsersTab = () => (
    <div className="settings-tab-content">
      <div className="tab-header">
        <h3>User Management</h3>
        <div className="header-actions">
          <button
            className="btn-secondary"
            onClick={fetchUsers}
            disabled={loading}
          >
            <FiRefreshCw className={loading ? "spinning" : ""} />
            Refresh
          </button>
          <button className="btn-primary">
            <Link to="/register" style={{ color: 'white', textDecoration: 'none' }}>
              <FiPlus /> Add User
            </Link>
          </button>
        </div>
      </div>

      <div className="search-box">
        <FiSearch className="search-icon" />
        <input
          type="text"
          placeholder="Search users by name, email, or role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {loading ? (
        <div className="loading">
          <FiRefreshCw className="spinning" /> Loading users...
        </div>
      ) : (
        <>
          <div className="users-stats">
            <span>Total Users: {users.length}</span>
            <span>Active: {users.filter((u) => u.isActive).length}</span>
            <span>
              developers: {users.filter((u) => u.role === "developer").length}
            </span>
          </div>

          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-users">
                      {searchTerm
                        ? "No users found matching your search"
                        : "No users found"}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user._id}>
                      <td>
                        <div className="user-info">
                          <div className="user-avatar">
                            {user.name ? user.name.charAt(0).toUpperCase() : <FiUser />}
                          </div>
                          <div className="user-name">
                            {user.name || "No Name"}
                          </div>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`role-badge role-${user.role}`}>
                          {user.role || "user"}
                        </span>
                      </td>
                      <td>
                        <button
                          className={`status-toggle ${
                            user.isActive ? "active" : "inactive"
                          }`}
                          onClick={() =>
                            handleStatusToggle(user._id, user.isActive)
                          }
                          title={
                            user.isActive ? "Deactivate user" : "Activate user"
                          }
                          disabled={currentUser?.role !== "developer"}
                        >
                          {user.isActive ? <FiCheckCircle /> : <FiXCircle />}
                          {user.isActive ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td>
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn-icon view" 
                            title="View Details"
                            onClick={() => handleViewUser(user)}
                          >
                            <FiEye />
                          </button>
                          <button 
                            className="btn-icon edit" 
                            title="Edit"
                            onClick={() => handleEditUser(user)}
                            disabled={currentUser?.role !== "developer"}
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            className="btn-icon delete"
                            title="Delete"
                            onClick={() => handleDeleteUser(user._id)}
                            disabled={
                              user._id === currentUser?._id ||
                              currentUser?.role !== "developer"
                            }
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
//here code

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage users and authentication settings</p>
      </div>

      <div className="settings-tabs">
        <div className="tabs-navigation">
          <button
            className={`tab-btn ${activeTab === "users" ? "active" : ""}`}
            onClick={() => setActiveTab("users")}
          >
            <FiUsers /> User Management
          </button>
          {/* <button
            className={`tab-btn ${activeTab === "auth" ? "active" : ""}`}
            onClick={() => setActiveTab("auth")}
          >
            <FiShield /> Authentication
          </button> */}
        </div>

        <div className="tabs-content">
          {activeTab === "users" && renderUsersTab()}
          {/* {activeTab === "auth" && renderAuthSettingsTab()} */}
        </div>
      </div>

      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            {renderUserModal()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;